import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 3: Sacred Geometry
 * Flower of Life pattern with central circle and 6 surrounding circles
 */
export default function FlowerOfLife({ audioData, isActive, position = [0, 0, 0] }) {
  const groupRef = useRef()
  const circleRefs = useRef([])

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Calculate positions for 6 circles around center
  const circlePositions = useMemo(() => {
    const positions = []
    const radius = 1.8
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2
      positions.push([
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        0
      ])
    }
    return positions
  }, [])

  // Pre-clone materials for each circle (7 total: 6 + 1 center)
  // Cloned once in useMemo, not per render
  const holographicMaterials = useMemo(() => {
    const base = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        intensity: { value: 1.0 },
        color1: { value: new THREE.Color('#8B00FF') },
        color2: { value: new THREE.Color('#FF00FF') },
        color3: { value: new THREE.Color('#00FFFF') }
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float intensity;
        uniform vec3 color1;
        uniform vec3 color2;
        uniform vec3 color3;
        varying vec2 vUv;
        varying vec3 vNormal;

        void main() {
          float pattern = sin(vUv.x * 10.0 + time) * sin(vUv.y * 10.0 + time);
          float fresnel = pow(1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0))), 2.0);

          vec3 color = mix(color1, color2, pattern * 0.5 + 0.5);
          color = mix(color, color3, fresnel);

          float alpha = 0.6 + pattern * 0.2 + fresnel * 0.2;

          gl_FragColor = vec4(color * intensity, alpha);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide
    })
    return Array.from({ length: 7 }, () => base.clone())
  }, [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Update holographic shader time on all material clones
    holographicMaterials.forEach((mat) => {
      if (mat.uniforms) {
        mat.uniforms.time.value = time
        mat.uniforms.intensity.value = 1 + amplitude * 0.5
      }
    })

    // Rotate entire group
    if (groupRef.current) {
      groupRef.current.rotation.z += delta * 0.1
    }

    // Scale each circle based on different FFT bands
    circleRefs.current.forEach((circle, index) => {
      if (circle) {
        const fftIndex = Math.floor((index / 6) * fft.length)
        const fftValue = fft[fftIndex] || 0

        // Individual rotation
        circle.rotation.z += delta * (0.15 + index * 0.03)

        // Scale based on FFT band
        const scale = 1 + fftValue * 0.3
        circle.scale.setScalar(scale)

        // Shimmer effect
        if (circle.material.uniforms) {
          circle.material.uniforms.intensity.value = 1 + fftValue * 2
        }
      }
    })
  })

  return (
    <group ref={groupRef} position={position}>
      {/* Central circle */}
      <mesh ref={(el) => (circleRefs.current[6] = el)}>
        <ringGeometry args={[0.8, 1.0, 32]} />
        <primitive object={holographicMaterials[6]} attach="material" />
      </mesh>

      {/* 6 surrounding circles */}
      {circlePositions.map((pos, index) => (
        <mesh
          key={index}
          ref={(el) => (circleRefs.current[index] = el)}
          position={pos}
        >
          <ringGeometry args={[0.8, 1.0, 32]} />
          <primitive object={holographicMaterials[index]} attach="material" />
        </mesh>
      ))}

      {/* Additional decorative circles for depth */}
      {circlePositions.map((pos, index) => (
        <mesh
          key={`inner-${index}`}
          position={[pos[0], pos[1], -0.5]}
        >
          <ringGeometry args={[0.6, 0.7, 32]} />
          <meshBasicMaterial
            color="#8B00FF"
            transparent
            opacity={0.3}
          />
        </mesh>
      ))}

      {/* Center glow */}
      <pointLight position={[0, 0, 1]} intensity={2} color="#FF00FF" distance={6} />
    </group>
  )
}
